import { omit, mapValues } from 'lodash';

import {
  ACCOUNT_CHANGE,
  ACCOUNT_WALLET_RESET,
  ACCOUNT_WALLET_SELECT,
  PAYMENT_SEND_START,
  PAYMENT_SEND_SUBMITTED,
  PAYMENT_SEND_ABORTED,
  PAYMENT_SEND_FAILED,
  PAYMENT_SEND_CONFIRMED,
  PAYMENT_SEND_REJECTED,
  PAYMENT_ACCEPT_START,
  PAYMENT_ACCEPT_SUBMITTED,
  PAYMENT_ACCEPT_ABORTED,
  PAYMENT_ACCEPT_FAILED,
  PAYMENT_ACCEPT_CONFIRMED,
  PAYMENT_ACCEPT_REJECTED,
  PAYMENT_CANCEL_START,
  PAYMENT_CANCEL_SUBMITTED,
  PAYMENT_CANCEL_ABORTED,
  PAYMENT_CANCEL_CONFIRMED,
  PAYMENT_CANCEL_REJECTED,
  PAYMENTS_FETCHED,
  PAYMENTS_FETCH_FAILED,
  PAYMENT_DISMISSED,
  PAYMENT_PENDING,
  PAYMENT_LOCALLY_DISCARDED,
  PAYMENTS_LOCAL_LOADED,
} from '../actionTypes';
import createStatus from '../utilities/PaymentStatus';
import { PaymentStatus, statusMapping } from '../constants';

const getDefaultState = () => ({
  fetchedPayments: {},
  localPayments: {},
  paymentsFetching: false,
});

const updateLocalPayment = (state, txHash, modifier) => ({
  ...state,
  localPayments: {
    ...state.localPayments,
    [txHash]: modifier(state.localPayments[txHash] || {}),
  },
});

// TODO test: assert graceful merge
const moveToFetched = (state, transactionHash) => ({
  ...state,
  localPayments: omit(state.localPayments, transactionHash),
  fetchedPayments: {
    ...state.fetchedPayments,
    [transactionHash]: {
      ...(state.fetchedPayments[transactionHash] || {}),
      ...state.localPayments[transactionHash],
    },
  },
});

const reducer = (state = getDefaultState(), action) => {
  const { type } = action;
  const transactionHash = action.paymentTransactionHash || action.transactionHash;

  switch (type) {
    case ACCOUNT_CHANGE:
      return {
        ...state,
        localPayments: {},
        fetchedPayments: {},
        paymentsFetching: true,
      };

    case ACCOUNT_WALLET_RESET:
      return {
        localPayments: {},
        fetchedPayments: {},
        paymentsFetching: true,
      };

    case ACCOUNT_WALLET_SELECT:
      return {
        ...state,
        paymentsFetching: true,
      };

    case PAYMENTS_FETCHED:
      return {
        ...state,
        paymentsFetching: false,
        fetchedPayments: mapValues(action.payments, p => ({
          ...p,
          status: createStatus(statusMapping[p.status]),
        })),
      };

    case PAYMENTS_FETCH_FAILED:
      return {
        ...state,
        paymentsFetching: false,
      };

    case PAYMENT_SEND_START:
      return updateLocalPayment(state, 'draft', () => ({
        ...action.payment,
        id: '',
        type: 'outgoing',
        status: createStatus(PaymentStatus.sending),
      }));

    case PAYMENT_SEND_SUBMITTED:
      return {
        ...state,
        localPayments: omit(
          updateLocalPayment(state, action.transactionHash, () => ({
            ...state.localPayments.draft,
            sentTimestamp: action.sentTimestamp,
            contract: action.contract,
            status: createStatus(PaymentStatus.confirmingSend),
            transactionHash: action.transactionHash,
          })).localPayments,
          'draft',
        ),
      };

    case PAYMENT_SEND_ABORTED:
      return updateLocalPayment(state, 'draft', p => ({
        ...p,
        status: createStatus(PaymentStatus.draft),
      }));

    case PAYMENT_SEND_FAILED:
      return updateLocalPayment(state, 'draft', p => ({
        ...p,
        status: createStatus(PaymentStatus.failed),
      }));

    case PAYMENT_SEND_CONFIRMED:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.pending),
      }));

    case PAYMENT_PENDING: {
      return {
        ...state,
        // Remove from local, if it was there.
        localPayments: omit(state.localPayments, action.event.transactionHash),
        fetchedPayments: {
          ...state.fetchedPayments,
          [action.event.transactionHash]: {
            id: action.event.returnValues.id,
            amount: action.event.returnValues.amount,
            tokenAddress: action.event.returnValues.token,
            sender: action.event.returnValues.sender,
            destination: action.event.returnValues.destination,
            sentTimestamp: action.sentTimestamp,
            contract: action.event.contract,
            status: createStatus(PaymentStatus.pending),
            blockNumber: action.event.blockNumber,
            transactionHash: action.event.transactionHash,
            type: action.sendType,
          },
        },
      };
    }

    case PAYMENT_SEND_REJECTED:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.failed),
      }));

    case PAYMENT_ACCEPT_START:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.accepting),
      }));

    case PAYMENT_ACCEPT_ABORTED:
      return moveToFetched(
        updateLocalPayment(state, transactionHash, p => ({
          ...p,
          status: createStatus(PaymentStatus.pending),
        })),
        transactionHash,
      );

    case PAYMENT_ACCEPT_SUBMITTED:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.confirmingAccept),
      }));

    case PAYMENT_ACCEPT_FAILED:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.pending),
      }));

    case PAYMENT_ACCEPT_CONFIRMED:
      return moveToFetched(
        updateLocalPayment(state, transactionHash, p => ({
          ...p,
          status: createStatus(PaymentStatus.accepted),
        })),
        transactionHash,
      );

    case PAYMENT_ACCEPT_REJECTED:
      return moveToFetched(
        updateLocalPayment(state, transactionHash, p => ({
          ...p,
          status: createStatus(PaymentStatus.pending),
        })),
        transactionHash,
      );

    case PAYMENT_CANCEL_START:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.cancelling),
      }));

    case PAYMENT_CANCEL_ABORTED:
      return moveToFetched(
        updateLocalPayment(state, transactionHash, p => ({
          ...p,
          status: createStatus(PaymentStatus.pending),
        })),
        transactionHash,
      );

    case PAYMENT_CANCEL_SUBMITTED:
      return updateLocalPayment(state, transactionHash, p => ({
        ...p,
        status: createStatus(PaymentStatus.confirmingCancel),
      }));

    case PAYMENT_CANCEL_CONFIRMED:
      return moveToFetched(
        updateLocalPayment(state, transactionHash, p => ({
          ...p,
          status: createStatus(PaymentStatus.cancelled),
        })),
        transactionHash,
      );

    case PAYMENT_CANCEL_REJECTED:
      return moveToFetched(
        updateLocalPayment(state, transactionHash, p => ({
          ...p,
          status: createStatus(PaymentStatus.pending),
        })),
        transactionHash,
      );

    case PAYMENT_LOCALLY_DISCARDED:
    case PAYMENT_DISMISSED:
      return {
        ...state,
        localPayments: omit(state.localPayments, transactionHash),
      };

    case PAYMENTS_LOCAL_LOADED:
      return {
        ...state,
        localPayments: mapValues(action.payments, p => ({
          ...p,
          // XXX status should really be serializable
          status: createStatus(p.status.toString()),
        })),
      };

    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/payments.js